import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';

interface IpReputation {
  ip: string;
  vt_malicious_count: number | null;
  vt_suspicious_count: number | null;
  vt_error: string | null;
  abuseipdb_score: number | null;
  abuseipdb_total_reports: number | null;
  abuseipdb_error: string | null;
  country: string | null;
}

// Badge di reputazione per un IP pubblico: interroga on-demand GET /api/ip-reputation/:ip (cache
// server-side 24h, vedi routes/ipReputation.ts) solo per IP pubblici — non ha senso interrogare
// VirusTotal/AbuseIPDB per un IP privato. Va mostrato solo accanto a un IP già identificato come
// pubblico dal chiamante (AlertDetailPage usa isPrivateIp per deciderlo).
export default function IpReputationBadge({ ip }: { ip: string }) {
  const [rep, setRep] = useState<IpReputation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios.get(`/api/ip-reputation/${ip}`)
      .then(res => { if (!cancelled) setRep(res.data.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ip]);

  if (loading) return <div className="text-xs text-gray-400 mt-1">Checking reputation…</div>;
  if (error || !rep) return null;

  const vtOk = rep.vt_malicious_count !== null;
  const abuseOk = rep.abuseipdb_score !== null;
  const malicious = (vtOk && rep.vt_malicious_count! > 0) || (abuseOk && rep.abuseipdb_score! >= 50);
  const suspicious = !malicious && ((vtOk && rep.vt_suspicious_count! > 0) || (abuseOk && rep.abuseipdb_score! > 0));

  return (
    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
      <div className={`inline-flex items-center gap-1 text-xs font-medium ${
        malicious ? 'text-red-700' : suspicious ? 'text-amber-700' : 'text-emerald-700'}`}>
        {malicious ? <ShieldAlert size={12} /> : suspicious ? <ShieldQuestion size={12} /> : <ShieldCheck size={12} />}
        {malicious ? 'Flagged as malicious' : suspicious ? 'Some reports' : 'No reputation flags'}
      </div>
      {vtOk ? (
        <a href={`https://www.virustotal.com/gui/ip-address/${ip}`} target="_blank" rel="noreferrer"
          className="block text-xs text-gray-500 hover:text-primary-600 hover:underline">
          VirusTotal: {rep.vt_malicious_count} malicious / {rep.vt_suspicious_count} suspicious
        </a>
      ) : (
        <div className="text-xs text-gray-300" title={rep.vt_error || ''}>VirusTotal: unavailable</div>
      )}
      {abuseOk ? (
        <a href={`https://www.abuseipdb.com/check/${ip}`} target="_blank" rel="noreferrer"
          className="block text-xs text-gray-500 hover:text-primary-600 hover:underline">
          AbuseIPDB: {rep.abuseipdb_score}% confidence ({rep.abuseipdb_total_reports} reports)
        </a>
      ) : (
        <div className="text-xs text-gray-300" title={rep.abuseipdb_error || ''}>AbuseIPDB: unavailable</div>
      )}
    </div>
  );
}
